import { useEffect, useRef } from 'react';
import {
  CandlestickSeries,
  createChart,
  CrosshairMode,
  type IChartApi,
  type ISeriesApi,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { Candle } from '@pipyar/core';
import { chartPalette } from './chartTheme';
import { shiftBars } from './chartShift';
import {
  TimeWindowPrimitive,
  type PointMarker,
  type WindowBand,
} from './timeWindowPrimitive';
import { cn } from '@/lib/utils';
import { useLiveBar } from '@/lib/useLiveBar';

interface TimeWindowChartProps {
  candles: Candle[];
  bands: WindowBand[];
  points?: PointMarker[];
  /**
   * Bar slots beyond the last candle, added to the series as whitespace.
   *
   * Lightweight Charts' time scale only spans times present in its data, so a
   * marker in the future has no coordinate until these exist. Whitespace
   * entries extend the axis without drawing anything.
   */
  futureTimes?: number[];
  digits: number;
  theme: 'dark' | 'light';
  resetKey?: string;
  className?: string;
  /**
   * How many bars to show on open.
   *
   * Deliberately not `fitContent()`. The series carries ~1500 bars so the
   * statistics have something to work with, and fitting all of them squeezes
   * each candle to a couple of pixels — the window bands become hairlines and
   * the price action inside them is unreadable, which is the whole point of the
   * page. Showing a slice keeps candles legible; scrolling still reaches the rest.
   */
  visibleBars?: number;
  /**
   * A stored-time span to centre the opening view on, normally the newest window
   * occurrence.
   *
   * Without this, zooming in far enough to read the candles means the window is
   * usually off-screen: the Tokyo band sits at 03:00, so a view of the last ~8
   * hours misses it for most of the trading day. Anchoring on the occurrence
   * instead lets the zoom go tighter while guaranteeing the band is visible.
   */
  focus?: { from: number; to: number } | null;
}

export function TimeWindowChart({
  candles,
  bands,
  points = [],
  futureTimes = [],
  digits,
  theme,
  resetKey,
  className,
  visibleBars = 110,
  focus = null,
}: TimeWindowChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  // Last candle follows the pushed price between refetches.
  useLiveBar(seriesRef, candles);
  const primitiveRef = useRef<TimeWindowPrimitive | null>(null);
  /** resetKey the visible range was last applied for; null means "needs positioning". */
  const positionedForRef = useRef<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const palette = chartPalette(theme);

    const chart = createChart(container, {
      autoSize: true,
      layout: {
        background: { color: palette.background },
        textColor: palette.text,
        attributionLogo: false,
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      },
      grid: {
        vertLines: { color: palette.grid },
        horzLines: { color: palette.grid },
      },
      rightPriceScale: { borderColor: palette.border },
      timeScale: {
        borderColor: palette.border,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 6,
      },
      crosshair: {
        /*
         * Normal, not the library's default of Magnet.
         *
         * Magnet snaps the price readout to the nearest OHLC of the bar under
         * the pointer, so the crosshair jumps to an open or close instead of
         * following the mouse. That is useful for reading a candle and actively
         * wrong for measuring anything between them — judging where a knot sits
         * inside a body, or how far price is from a gap edge.
         */
        mode: CrosshairMode.Normal,
        vertLine: { color: palette.crosshair, labelBackgroundColor: palette.crosshair },
        horzLine: { color: palette.crosshair, labelBackgroundColor: palette.crosshair },
      },
      localization: {
        timeFormatter: (t: Time) => formatStoredTime(t as UTCTimestamp),
      },
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: palette.bull,
      downColor: palette.bear,
      borderUpColor: palette.bull,
      borderDownColor: palette.bear,
      wickUpColor: palette.bull,
      wickDownColor: palette.bear,
      priceFormat: { type: 'price', precision: digits, minMove: 10 ** -digits },
    });

    const primitive = new TimeWindowPrimitive();
    // Attached to the PANE, not the series: the bands span the full height and
    // are unrelated to any one series' price scale.
    chart.panes()[0]?.attachPrimitive(primitive);

    chartRef.current = chart;
    seriesRef.current = series;
    primitiveRef.current = primitive;

    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      primitiveRef.current = null;
      // A new chart instance has no view state, so the next data load must
      // reposition it even though the series has not changed.
      positionedForRef.current = null;
    };
  }, [theme, digits]);

  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;

    const bars = candles.map((c) => ({
      time: c.time as UTCTimestamp,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }));

    // Whitespace must be strictly after the last real bar and in ascending
    // order; setData rejects an unsorted or duplicated series outright.
    const last = candles.length > 0 ? candles[candles.length - 1]!.time : -Infinity;
    const filler = futureTimes
      .filter((t) => t > last)
      .sort((a, b) => a - b)
      .map((t) => ({ time: t as UTCTimestamp }));

    series.setData([...bars, ...filler]);
  }, [candles, futureTimes]);

  useEffect(() => {
    primitiveRef.current?.setBands(bands);
  }, [bands]);

  useEffect(() => {
    primitiveRef.current?.setPoints(points);
  }, [points]);

  // Position the view once per series, on the first data for that series.
  //
  // `candles.length` must be a dependency because the chart is created before
  // the data arrives and a range is meaningless with no bars to point at. But
  // acting on every change would re-centre the chart on each 30s refetch and
  // discard wherever the user had scrolled to. The guard fires it once per
  // resetKey — per symbol/timeframe — and stays out of the way after.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || candles.length === 0) return;

    const key = resetKey ?? '';
    if (positionedForRef.current === key) return;
    positionedForRef.current = key;

    const last = candles.length;
    let from = last - Math.min(visibleBars, last);
    let to = last + shiftBars(last - from);

    if (focus) {
      const start = candles.findIndex((c) => c.time >= focus.from);
      if (start !== -1) {
        let end = start;
        while (end + 1 < last && candles[end + 1]!.time <= focus.to) end++;

        // Spend the remaining width mostly on what came BEFORE the window: the
        // run-up is context for the move, whereas everything after it is
        // already visible as ordinary price action.
        const spare = Math.max(visibleBars - (end - start + 1), 0);
        const before = Math.round(spare * 0.6);
        from = Math.max(0, start - before);
        to = Math.min(last + 6, end + (spare - before));
      }
    }

    chart.timeScale().setVisibleLogicalRange({ from, to });
  }, [resetKey, candles, visibleBars, focus]);

  return (
    <div className={cn('relative', className)}>
      <div ref={containerRef} className="absolute inset-0" />
    </div>
  );
}

function formatStoredTime(t: UTCTimestamp): string {
  const d = new Date((t as number) * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(
    d.getUTCHours(),
  )}:${pad(d.getUTCMinutes())}`;
}
